import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useContext, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Accordion from '../../../components/Accordion';
import Avatar, { AvatarSize, AvatarStatus } from '../../../components/Avatar';
import Blockquote from '../../../components/Blockquote';
import Button from '../../../components/Button';
import CircledIcon from '../../../components/CircledIcon';
import Link from '../../../components/Link';
import CustomSelect from '../../../components/Select';
import useModal from '../../../hooks/useModal';
import { ProfileStore } from '../../../providers/profile';
import { getAllProfiles } from '../../../services/profiles';
import { capitalizeFirstLetter, stepScrollToTop } from '../../../utils/utils';
import ReceiveFormsMailModal from '../ReceiveFormsMailModal/ReceiveFormsMailModal';
import PendingBeneficiariesModal from './PendingBeneficiaries/PendingBeneficiariesModal';
import SubmittedSuccessful from './SubmittedSuccessful/SubmittedSuccessful';
import YourBeneficiaries from './YourBeneficiaries/YourBeneficiaries';

const BeneficiariesList = ({
  primaryProfile,
  secondaryProfiles,
  isHsa,
}: any) => {
  const hasPrimaryProfile = !!primaryProfile.id;
  const hasSecondaryProfiles = secondaryProfiles.length > 0;

  const hasProfiles = hasPrimaryProfile || hasSecondaryProfiles;

  const secondaryProfilesFraction = `${(100 / secondaryProfiles.length)
    .toFixed(2)
    .replace(/\.00/, '')}%`;

  return (
    <Container className="p-10 pt-2">
      {!hasProfiles && (
        <>
          <div>
            <Blockquote variant="warning-bg" lineVariant="warning">
              <div className={'mb-0 text-ligther-grey fw-bolder'}>
                <span>Missing Beneficiaries</span>
              </div>
              <div className={'mb-0 text-ligther-grey'}>
                <span>
                  You're missing primary and contingent beneficiaries for Basic
                  Life Insurance.
                </span>
              </div>
            </Blockquote>
          </div>
          <Row className={'d-flex align-items-center mt-10'}>
            <Col className={'col-3 col-lg-2'}>
              <img
                src={`${
                  process.env.PUBLIC_URL
                }/${'no-beneficiary-selected.png'}`}
                alt="no-beneficiary"
              />
            </Col>
            <Col className={'col-6'}>
              <span className={'d-block text-fs-6 text-ligther-grey fw-light'}>
                No Beneficiary Selected
              </span>
            </Col>
          </Row>
        </>
      )}
      {hasProfiles && isHsa && (
        <div>
          <Blockquote variant="warning-bg" lineVariant="warning">
            <div className={'mb-0 text-ligther-grey fw-bolder'}>
              <span>Beneficiary Update Pending</span>
            </div>
            <div className={'mb-0 text-ligther-grey'}>
              <span>
                Complete, sign and return a Beneficiary Designation
                Authorization Form to verify your beneficiaries.
              </span>
            </div>
          </Blockquote>
        </div>
      )}

      <Row className="px-5">
        {hasPrimaryProfile && (
          <Col className="col-12 col-lg-6 ">
            <Row className="border-bottom">
              <Col className="text-fs-6 pt-11 pb-3 ps-0">Primary</Col>
            </Row>
            <Row className="text-fs-6 fw-light lh-xs pt-4 flex-nowrap">
              <Col className="ps-0 d-flex align-items-center">
                <div className="pe-3">
                  <Avatar
                    id={primaryProfile.id}
                    status={AvatarStatus.ENABLED}
                    size={AvatarSize.SMALL}
                    name={`${primaryProfile.name}`}
                  />
                </div>

                <div>
                  <div className="text-nowrap">{`${primaryProfile.name}`}</div>
                  <div className="text-ligther-grey">
                    {capitalizeFirstLetter(primaryProfile.relationship)}
                  </div>
                </div>
              </Col>
              <Col className="text-end text-indigo pe-0">100%</Col>
            </Row>
          </Col>
        )}
        {hasSecondaryProfiles && (
          <Col className="col-12 col-lg-6 ps-lg-11 ">
            <Row className="border-bottom ">
              <Col className="text-fs-6 pt-11 pb-3 ps-0">Contingent</Col>
            </Row>
            {secondaryProfiles.map((profile: any, index: number) => {
              return (
                <Row
                  key={index}
                  className="text-fs-6 fw-light lh-xs pt-4 flex-nowrap"
                >
                  <Col className="ps-0 d-flex align-items-center">
                    <div className="pe-3">
                      <Avatar
                        id={profile.id}
                        status={AvatarStatus.ENABLED}
                        size={AvatarSize.SMALL}
                        name={`${profile.name}`}
                      />
                    </div>

                    <div>
                      <div className="text-nowrap">{`${profile.name}`}</div>
                      <div className="text-ligther-grey">
                        {capitalizeFirstLetter(profile.relationship)}
                      </div>
                    </div>
                  </Col>
                  <Col className="text-end text-indigo pe-0">
                    {secondaryProfilesFraction}
                  </Col>
                </Row>
              );
            })}
          </Col>
        )}
      </Row>
    </Container>
  );
};

const BeneficiariesContent = () => {
  const [
    showBeneficiariesModal,
    handleShowBeneficiariesModal,
    handleCloseBeneficiariesModal,
  ] = useModal();

  const [
    showReceiveMailModal,
    handleShowReceiveMailModal,
    handleCloseReceiveMailModal,
  ] = useModal();

  const [requiredActions, setActionsRequired] = useState(true);

  const [submittedScreen, setSubmittedScreen] = useState(false);

  const [successfulScreen, setSuccessScreen] = useState(false);

  const navigate = useNavigate();

  const profiles = getAllProfiles().data.filter(
    (profile) => profile.relationship !== 'self'
  );

  const handleDelete = () => {
    console.log('Open delete modal');
  };

  const handleEdit = () => {
    console.log('Open edit modal');
  };

  const options = [
    { value: '1', label: 'Start' },
    { value: '2', label: 'Submitted' },
    { value: '3', label: 'Success' },
  ];

  const handleOnStateChange = (e: any) => {
    if (e === '1') {
      sessionStorage.setItem('isActionsRequired', '1');
      setActionsRequired(true);
      setSubmittedScreen(false);
      setSuccessScreen(false);
    } else if (e === '2') {
      sessionStorage.removeItem('isActionsRequired');
      setSubmittedScreen(true);
      setSuccessScreen(false);
      setActionsRequired(false);
    } else if (e === '3') {
      sessionStorage.removeItem('isActionsRequired');

      setSuccessScreen(true);
      setActionsRequired(false);
      setSubmittedScreen(false);
    }
  };

  useEffect(() => {
    const isActionsRequired = sessionStorage.getItem('isActionsRequired');
    if (isActionsRequired === '1') {
      setActionsRequired(true);
    }
  }, []);

  const { basicList, hsaList } = useContext(ProfileStore);

  const hasPrimaryProfile = !!basicList.beneficiary.id;
  const hasSecondaryProfiles = basicList.contingents.length > 0;

  const hasProfiles = hasPrimaryProfile || hasSecondaryProfiles;

  const handleLinkRoute = () => {
    sessionStorage.setItem('isActionsRequired', '1');
    setActionsRequired(true);
    setSubmittedScreen(false);
    setSuccessScreen(false);
  };

  useEffect(() => {
    stepScrollToTop();
  }, [requiredActions, submittedScreen, successfulScreen]);

  return (
    <>
      {requiredActions && (
        <>
          <section className={'w-100 d-flex justify-content-between mt-7'}>
            <div className={'d-flex align-items-center'}>
              <CircledIcon
                variant={'icon-emphasized'}
                icon={light('hands-holding-child')}
              />
              <h4 className={'ms-7 mb-0 text-sm-20-lg-32'}>Beneficiaries</h4>
            </div>
          </section>
          <section className="d-flex flex-column flex-lg-row justify-content-lg-between mt-11">
            <div className="col-lg-7">
              <div className="d-flex flex-column px-10 py-11 px-lg-11 bg-white solid-salmon-border">
                <section className="d-flex justify-content-start">
                  <h5 className="m-0">Action Required</h5>
                </section>
                <section className="d-flex justify-content-start mt-7">
                  <span className="fw-normal text-sub1 lh-xs">
                    Beneficiary Update Pending
                  </span>
                </section>
                <section className="d-flex justify-content-start mt-7">
                  <span className="fw-light text-sub1 lh-xs">
                    You are required to complete, sign and return a Beneficiary
                    Designation Authorization Form. You can request another copy
                    of the form via mail or you can print it yourself.
                  </span>
                </section>
                <section className="mt-7">
                  <div className="lh-xs pt-1 d-flex align-items-center">
                    <Button
                      onClick={handleShowReceiveMailModal}
                      leftIcon={light('mailbox')}
                      variant="danger-outline"
                      className={'order-1 order-lg-3 mt-7 mt-lg-0 me-7'}
                      size="sm"
                    >
                      Mail
                    </Button>
                    <ReceiveFormsMailModal
                      showModal={showReceiveMailModal}
                      closeModal={handleCloseReceiveMailModal}
                    />
                    <Link
                      href={`${
                        process.env.PUBLIC_URL
                      }/${'blank-demo-example.pdf'}`}
                      target={'_blank'}
                    >
                      <Button
                        leftIcon={light('file-arrow-down')}
                        variant="danger-outline"
                        className={'order-1 order-lg-3 mt-7 mt-lg-0'}
                        size="sm"
                      >
                        Download/Print
                      </Button>
                    </Link>
                  </div>
                  <div className="lh-xs pt-7 d-flex align-items-center">
                    <span className="text-sub1 fw-light">
                      Until we receive and approve your paperwork, you can view
                      your pending beneficiaries.
                    </span>
                  </div>
                </section>
                <section className="d-flex flex-column flex-lg-row justify-content-lg-between mt-7">
                  <div>
                    <Button
                      variant="primary"
                      className={'order-1 order-lg-3 mt-7 mt-lg-0'}
                      onClick={handleShowBeneficiariesModal}
                    >
                      Pending Beneficiaries
                    </Button>
                    <PendingBeneficiariesModal
                      showBeneficiariesModal={showBeneficiariesModal}
                      handleCloseBeneficiariesModal={
                        handleCloseBeneficiariesModal
                      }
                    />
                  </div>
                </section>
              </div>

              <div className="mt-10">
                <Accordion
                  title={'Basic Life Insurance'}
                  subtitle={''}
                  infoIcon={
                    !hasProfiles ? light('circle-exclamation') : undefined
                  }
                  button={
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={light('edit')}
                      onClick={(e) => {
                        navigate(`?q=beneficiaries`);
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      Edit
                    </Button>
                  }
                >
                  <BeneficiariesList
                    primaryProfile={basicList.beneficiary}
                    secondaryProfiles={basicList.contingents}
                    isHsa={false}
                  />
                </Accordion>
              </div>
              <div className="mt-10">
                <Accordion
                  title={'HSA Investment'}
                  subtitle={''}
                  infoIcon={light('circle-exclamation')}
                  button={
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={light('edit')}
                      onClick={(e) => {
                        navigate(`?q=hsa`);
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      Edit
                    </Button>
                  }
                >
                  <BeneficiariesList
                    primaryProfile={hsaList.beneficiary}
                    secondaryProfiles={hsaList.contingents}
                    isHsa={true}
                  />
                </Accordion>
              </div>
            </div>
            <section className="mt-10 mt-lg-0">
              <YourBeneficiaries
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                data={profiles}
              />
            </section>
          </section>
        </>
      )}
      {submittedScreen && <SubmittedSuccessful spouseSelected={false} />}
      {successfulScreen && (
        <SubmittedSuccessful onClick={handleLinkRoute} spouseSelected />
      )}

      <section className={'mt-7 d-flex align-items-center'}>
        <span className={'me-4 text-fs-6 fw-light'}>
          Select Menu to switch between the different states
        </span>
        <Col className={'col-4'}>
          <CustomSelect options={options} onChange={handleOnStateChange} />
        </Col>
      </section>
    </>
  );
};

export default BeneficiariesContent;
